/**
 * Argus Live — host-side postMessage core (PR AL-B1).
 *
 * Pure, framework-free state machine that:
 *  - Validates inbound `MessageEvent.data` against the Zod
 *    discriminated union from `@argusai/ai-core/argus-live/runtime/post-message-protocol`.
 *  - Enforces sender identity by comparing `event.source` against the
 *    iframe's `contentWindow` (the iframe is null-origin sandboxed —
 *    `event.origin === 'null'`, so the only authoritative trust signal
 *    is the Window identity the host itself mounted).
 *  - Enforces the §4.4 resource caps:
 *      * ≤ 200 KB postMessage payload per 60s rolling window
 *      * ≤ 30 FETCH_DATA requests per 60s rolling window per dashboard
 *      * RESIZE clamped to 20 000 px (also enforced by the Zod schema)
 *  - Rejects any non-allowlisted `type` with a no-op + structured warn
 *    (defense-in-depth on top of the Zod discriminated union, since
 *    later schema bumps may broaden the allowed list and we want a hard
 *    invariant the host respects right now).
 *
 * Threat model anchors (see ARGUS-LIVE-SPEC §4.5 row 7):
 *   - postMessage forgery from a cross-origin attacker page is blocked
 *     at the source-identity check; even if the attacker guesses the
 *     right window pair, the Zod schema rejects malformed payloads.
 *   - A compromised iframe (XSS-via-data, prototype pollution surviving
 *     the freeze) cannot exfiltrate by spamming the host because every
 *     payload is byte-counted into a 60s budget that, once exceeded,
 *     freezes additional sends and emits `dashboard.runtime.throttled`.
 *   - The host never `JSON.parse`s the inbound payload — `MessageEvent.data`
 *     is already structured-cloned by the browser, and we pass it
 *     directly to `parseIframeToHostEvent` from the protocol module.
 *
 * Coordination with sibling PRs:
 *   - AL-B2 (data-fetch proxy) consumes the `dispatch.fetchData`
 *     interface this file exports — its REST handler is the recipient
 *     of every FETCH_DATA the host accepts.
 *   - AL-B3 (3-pane UI) consumes the React hook from
 *     `use-argus-live-dashboard-host.tsx`, not this file directly.
 *   - AL-B4 (Ask Argus author agent) does not interact with the host
 *     listener; it operates one layer up at the chat-pane level.
 *
 * Why a pure core + a thin React hook (not one big component):
 *   - The web package has no jsdom in vitest, so DOM-bearing tests are
 *     limited. Splitting the logic out lets us test ≥30 cases of cap
 *     enforcement / identity rejection / dispatch routing without
 *     touching `window` or `addEventListener`.
 *   - The hook itself is a thin wrapper that translates window events
 *     into core method calls — it is exercised end-to-end by the AL-B5
 *     Playwright suite.
 */

import {
  IFRAME_TO_HOST_EVENT_TYPES,
  parseIframeToHostEvent,
  type IframeToHostEvent,
  type IframeToHostEventType,
  type HostToIframeEvent,
} from '@argusai/ai-core/argus-live/runtime/post-message-protocol';
import type { ExportControl, Sensitivity } from '@argusai/shared';

// ─── Caps ──────────────────────────────────────────────────────────────

/**
 * Shape of the host caps. Declared as an explicit interface so
 * `Partial<HostCaps>` overrides accept any number value (the literal
 * defaults would otherwise be inferred as their concrete values and
 * widening becomes impossible).
 */
export interface HostCaps {
  /** Sliding window for both byte and fetch caps. */
  windowMs: number;
  /** Total postMessage payload bytes per dashboard per window. */
  maxBytesPerWindow: number;
  /** Max FETCH_DATA requests per dashboard per window. */
  maxFetchesPerWindow: number;
  /** Per-message hard cap. A single message > this is dropped
   *  unconditionally with `OVERSIZE_MESSAGE` even if the rolling window
   *  has room — protects against a single 50 MB payload eating the
   *  whole render thread's structured-clone budget. */
  maxBytesPerMessage: number;
  /** RESIZE upper bound. The schema already enforces ≤ 20 000 px, but
   *  we restate the cap so the React hook can clamp the value the host
   *  applies to the iframe's outer container without re-deriving it
   *  from the Zod shape. */
  maxIframeHeightPx: number;
}

/**
 * Hard caps from ARGUS-LIVE-SPEC §4.4. Exposed as constants (not
 * configuration) because every deployment ships the same numbers — they
 * are part of the platform invariant set and changing them requires a
 * spec amendment.
 */
export const POST_MESSAGE_HOST_CAPS: Readonly<HostCaps> = Object.freeze({
  windowMs: 60_000,
  maxBytesPerWindow: 200 * 1024,
  maxFetchesPerWindow: 30,
  maxBytesPerMessage: 200 * 1024,
  maxIframeHeightPx: 20_000,
});

// ─── Reasons for rejection / throttle ──────────────────────────────────

/**
 * Reasons a message is dropped at the host. Surfaced in the structured
 * warn log + (where relevant) emitted as `dashboard.runtime.throttled`
 * via the audit-event dispatcher. Kept as a string-enum so AL-B2's
 * server-side throttle counters can correlate with the same labels.
 */
export type HostMessageRejection =
  | 'SOURCE_MISMATCH'
  | 'UNKNOWN_TYPE'
  | 'INVALID_SCHEMA'
  | 'OVERSIZE_MESSAGE'
  | 'RATE_LIMITED_BYTES'
  | 'RATE_LIMITED_FETCHES'
  | 'HANDLER_FAILED';

/**
 * Result of `handleMessage`. Tests assert against this shape rather
 * than against side effects; the side effects (dispatcher calls, log
 * lines) are themselves tested independently via spy injection.
 */
export type HostMessageOutcome =
  | { kind: 'accepted'; type: IframeToHostEventType }
  | { kind: 'rejected'; reason: HostMessageRejection; details?: string };

// ─── Dispatcher injection ──────────────────────────────────────────────

/**
 * Set of audit-event types the iframe is allowed to emit. Mirrors the
 * Zod enum in `post-message-protocol.ts` but restated here so a future
 * schema bump doesn't silently widen the allowed list at the
 * dispatcher boundary. AL-B2's server-side handler restates the same
 * list (defense in depth).
 */
export type ArgusLiveAuditEventType =
  | 'dashboard.runtime.crashed'
  | 'dashboard.runtime.throttled'
  | 'dashboard.runtime.binding.requested';

/**
 * The host doesn't perform network I/O itself; it delegates to a
 * dispatcher contract that AL-B2 (data-fetch proxy) implements and AL-B5
 * (Playwright) replaces with a fake. Keeping this as a plain interface
 * means the core can be tested with a spy without import-mocking
 * `fetch`.
 *
 * Every method is async-throwing-or-resolving — failure is communicated
 * back to the iframe as a Host-direction ERROR event, then a host log
 * line. Resolving with the relevant payload causes the host to fire a
 * DATA_RESPONSE.
 *
 * Stable interface declaration (AL-B2 must implement this).
 */
export interface ArgusLiveHostDispatcher {
  /**
   * Routes a FETCH_DATA request to `POST /api/v1/argus-live/data-fetch`.
   * AL-B2's implementation re-checks classification, dispatches through
   * the MCP gateway, and returns the {data, classification, dataAsOf}
   * triple that the host wraps into DATA_RESPONSE.
   */
  fetchData(input: {
    dashboardId: string;
    versionId?: string | null;
    bindingId: string;
    requestId: string;
    params?: Record<string, unknown>;
  }): Promise<{
    data: unknown;
    classification: { sensitivity: string; exportControl: string };
    dataAsOf: string;
  }>;

  /**
   * Cockpit Phase 3 (§2.C) — routes an EXECUTE_ACTION request to
   * `POST /api/v1/argus-live/dashboards/:id/actions/:actionId/execute`. The
   * server mints the ExecutionAuthorization (the iframe NEVER mints authz)
   * and either dispatches the write or creates an approval row. Returns the
   * outcome the host relays back into the iframe as ACTION_RESULT.
   *
   * A FORBIDDEN / ERROR outcome RESOLVES (it is a valid result the dashboard
   * renders) — only a transport failure rejects, which the host maps to an
   * ACTION_RESULT with status='error'.
   */
  executeAction(input: {
    dashboardId: string;
    actionId: string;
    requestId: string;
    inputOverride?: Record<string, unknown>;
  }): Promise<{
    status: 'executed' | 'pending_approval' | 'forbidden' | 'error';
    approvalId?: string;
    result?: unknown;
    code?: string;
    message?: string;
  }>;

  /**
   * Cockpit Phase 4 (§2.A) — routes a READ_STATE / WRITE_STATE request to the
   * STATE endpoints (GET/POST .../state, GET/PATCH .../user-state). The server
   * sets organization_id from authContext (the iframe NEVER writes the DB) and
   * classification-gates every row. Returns the outcome the host relays back as
   * STATE_RESPONSE. A FORBIDDEN / ERROR outcome RESOLVES (the dashboard renders
   * the right state); only a transport failure rejects → status='error'.
   */
  stateOp(input: {
    dashboardId: string;
    requestId: string;
    op: 'read_rows' | 'write_row' | 'read_user_state' | 'write_user_state';
    tableSlug: string;
    rowKey?: string;
    query?: { filter?: Record<string, unknown>; limit?: number; cursor?: string };
    data?: Record<string, unknown>;
    classification?: {
      sensitivity: Sensitivity;
      exportControl: ExportControl;
    };
  }): Promise<{
    status: 'ok' | 'forbidden' | 'error';
    data?: unknown;
    code?: string;
    message?: string;
  }>;

  /**
   * Routes an AUDIT_EVENT to `POST /api/v1/argus-live/audit-events`.
   * AL-B2 strips PII server-side before persisting; the iframe-supplied
   * `metadata` payload is treated as untrusted.
   */
  emitAudit(input: {
    dashboardId: string;
    eventType: ArgusLiveAuditEventType;
    metadata?: Record<string, unknown>;
  }): Promise<void>;

  /**
   * Posts a HostToIframe event back to the iframe. The host implementation
   * does `iframeRef.contentWindow.postMessage(payload, '*')` — origin is
   * `'*'` because the iframe is null-origin. (Browser still scopes the
   * delivery to the contentWindow we hold, so this is safe.)
   */
  sendToIframe(event: HostToIframeEvent): void;
}

// ─── Logger injection ──────────────────────────────────────────────────

/**
 * Structured-log surface for the host. The web package has no Pino, so
 * we accept any object that quacks like `{warn, info, error}`. Default
 * implementation thinly wraps `console` with a stable prefix; the
 * Playwright suite (AL-B5) swaps in a spy.
 */
export interface ArgusLiveHostLogger {
  warn(payload: Record<string, unknown>, message: string): void;
  info(payload: Record<string, unknown>, message: string): void;
  error(payload: Record<string, unknown>, message: string): void;
}

export const consoleHostLogger: ArgusLiveHostLogger = {
  warn(payload, message) {
    // eslint-disable-next-line no-console
    console.warn(`[argus-live:host] ${message}`, payload);
  },
  info(payload, message) {
    // eslint-disable-next-line no-console
    console.info(`[argus-live:host] ${message}`, payload);
  },
  error(payload, message) {
    // eslint-disable-next-line no-console
    console.error(`[argus-live:host] ${message}`, payload);
  },
};

// ─── Sliding window byte/fetch counter ─────────────────────────────────

/**
 * Token-bucket-equivalent rolling-window counter. Stored as an array of
 * timestamped entries; we trim from the head every time we record or
 * query. The expected number of entries inside a 60s window is bounded
 * by the fetch cap (30) plus a handful of audit events, so an O(n)
 * trim is acceptable — no need for a circular buffer or skip list.
 *
 * Test note: the counter accepts a `now: () => number` so tests can run
 * deterministically without `vi.useFakeTimers()`.
 */
export class SlidingWindowCounter {
  private entries: Array<{ at: number; weight: number }> = [];

  constructor(
    private readonly limit: number,
    private readonly windowMs: number,
    private readonly now: () => number = Date.now,
  ) {}

  /**
   * Returns `true` and records the entry when the proposed weight fits
   * inside the current window; `false` (no record) when it would push
   * us over the limit. Atomic by construction (sync).
   */
  tryRecord(weight: number = 1): boolean {
    const at = this.now();
    this.trim(at);
    const used = this.entries.reduce((acc, e) => acc + e.weight, 0);
    if (used + weight > this.limit) return false;
    this.entries.push({ at, weight });
    return true;
  }

  /**
   * Snapshot helpers for diagnostics + tests.
   */
  currentUsage(): number {
    this.trim(this.now());
    return this.entries.reduce((acc, e) => acc + e.weight, 0);
  }

  reset(): void {
    this.entries = [];
  }

  private trim(now: number): void {
    const cutoff = now - this.windowMs;
    // Fast-path: nothing aged out. Otherwise slice from the first
    // non-aged index — we use `findIndex` rather than a while-loop to
    // keep the algorithm one branch.
    const firstFresh = this.entries.findIndex((e) => e.at > cutoff);
    if (firstFresh === -1) {
      // All aged out.
      this.entries = [];
    } else if (firstFresh > 0) {
      this.entries = this.entries.slice(firstFresh);
    }
  }
}

// ─── Host instance ─────────────────────────────────────────────────────

/**
 * Per-dashboard host. Holds the rate-limit counters, the dispatcher,
 * and the source-identity reference. One instance per mounted iframe
 * — the React hook creates and tears it down with the iframe.
 */
export interface PostMessageHostOptions {
  /** Dashboard ID the host is bound to. Included in every dispatched
   *  call so AL-B2 can scope rate limits + audit rows. */
  dashboardId: string;
  /** Dispatcher implementation (AL-B2 supplies the prod variant; tests
   *  supply a spy). */
  dispatcher: ArgusLiveHostDispatcher;
  /** Optional structured logger. Defaults to `consoleHostLogger`. */
  logger?: ArgusLiveHostLogger;
  /** Optional clock (defaults to `Date.now`). Tests use a fake clock
   *  to advance the sliding window deterministically. */
  now?: () => number;
  /** Override the global cap constants for unit tests. Production
   *  callers leave this unset. */
  caps?: Partial<HostCaps>;
}

export class PostMessageHost {
  private readonly dashboardId: string;
  private readonly dispatcher: ArgusLiveHostDispatcher;
  private readonly logger: ArgusLiveHostLogger;
  private readonly now: () => number;
  private readonly caps: HostCaps;
  private readonly byteCounter: SlidingWindowCounter;
  private readonly fetchCounter: SlidingWindowCounter;

  /**
   * The expected sender. Set once by the host immediately after the
   * iframe is mounted — see `bindSource`. A null value means the host
   * has not yet been bound; every message is rejected with
   * `SOURCE_MISMATCH` until binding completes. This avoids accepting an
   * early bogus message during the iframe's load race.
   */
  private expectedSource: Window | null = null;

  /** Set of audit-event types accepted from the iframe. Mirrors the
   *  Zod enum but restated locally so a stray schema bump cannot
   *  silently broaden the allowed list. */
  private static readonly AUDIT_EVENT_TYPES = new Set<string>([
    'dashboard.runtime.crashed',
    'dashboard.runtime.throttled',
    'dashboard.runtime.binding.requested',
  ]);

  constructor(options: PostMessageHostOptions) {
    this.dashboardId = options.dashboardId;
    this.dispatcher = options.dispatcher;
    this.logger = options.logger ?? consoleHostLogger;
    this.now = options.now ?? Date.now;
    this.caps = { ...POST_MESSAGE_HOST_CAPS, ...(options.caps ?? {}) };
    this.byteCounter = new SlidingWindowCounter(
      this.caps.maxBytesPerWindow,
      this.caps.windowMs,
      this.now,
    );
    this.fetchCounter = new SlidingWindowCounter(
      this.caps.maxFetchesPerWindow,
      this.caps.windowMs,
      this.now,
    );
  }

  /**
   * Binds the host to a specific window — typically
   * `iframeRef.contentWindow`. MUST be called before any message will
   * be accepted; if the iframe is recreated, call again with the new
   * contentWindow.
   */
  bindSource(window: Window | null): void {
    this.expectedSource = window;
  }

  /**
   * Resets the rate-limit counters. Used when the iframe explicitly
   * reloads (the spec treats a fresh mount as a fresh execution from
   * the audit chain's perspective, so the 60s budgets reset too).
   */
  resetCounters(): void {
    this.byteCounter.reset();
    this.fetchCounter.reset();
  }

  /**
   * Main entry point. Accepts a `MessageEvent` and returns an outcome.
   * The outcome is returned (not thrown) so the React hook can update
   * its `lastError` state without try/catching.
   */
  async handleMessage(event: MessageEvent): Promise<HostMessageOutcome> {
    // 1. Source-identity check (THE critical defense vs forgery).
    if (
      this.expectedSource === null ||
      event.source !== this.expectedSource
    ) {
      this.logger.warn(
        {
          dashboardId: this.dashboardId,
          eventOrigin: event.origin,
        },
        'argus-live:host source-identity mismatch — message dropped',
      );
      return { kind: 'rejected', reason: 'SOURCE_MISMATCH' };
    }

    // 2. Per-message size cap (cheap O(1) JSON.stringify byte count).
    //    We use TextEncoder for accurate UTF-8 byte length; some payloads
    //    contain multi-byte chars (e.g. emoji in user-authored audit
    //    metadata) and a naive `.length` overcounts ASCII and
    //    undercounts surrogate pairs.
    let serialized: string;
    let byteLength: number;
    try {
      serialized = JSON.stringify(event.data);
      byteLength = encoderByteLength(serialized);
    } catch (err) {
      this.logger.warn(
        {
          dashboardId: this.dashboardId,
          err: (err as Error).message,
        },
        'argus-live:host could not serialize MessageEvent.data — message dropped',
      );
      return { kind: 'rejected', reason: 'INVALID_SCHEMA' };
    }

    if (byteLength > this.caps.maxBytesPerMessage) {
      this.logger.warn(
        {
          dashboardId: this.dashboardId,
          byteLength,
          cap: this.caps.maxBytesPerMessage,
        },
        'argus-live:host oversize message dropped',
      );
      // Best-effort report to the iframe runtime so the dashboard knows
      // its message did not land. We do NOT count this toward
      // `dashboard.runtime.throttled` — it is a structural failure of
      // the iframe code, not a rate-limit signal.
      this.safeSendError('INVALID_REQUEST', 'Message exceeds size cap');
      return {
        kind: 'rejected',
        reason: 'OVERSIZE_MESSAGE',
        details: `${byteLength} bytes > ${this.caps.maxBytesPerMessage}`,
      };
    }

    // 3. Rolling-byte cap.
    if (!this.byteCounter.tryRecord(byteLength)) {
      this.logger.warn(
        {
          dashboardId: this.dashboardId,
          byteLength,
          usage: this.byteCounter.currentUsage(),
          cap: this.caps.maxBytesPerWindow,
        },
        'argus-live:host byte-rate-limit hit — message dropped',
      );
      this.safeSendError('RATE_LIMITED', 'postMessage byte cap exceeded');
      this.safeEmitThrottleAudit('byte');
      return { kind: 'rejected', reason: 'RATE_LIMITED_BYTES' };
    }

    // 4. Zod schema validation — only AFTER the cheaper checks above so
    //    we don't burn schema CPU on payloads we'd drop anyway. The
    //    parser is total: returns null on any failure, never throws.
    const parsed = parseIframeToHostEvent(event.data);
    if (parsed === null) {
      this.logger.warn(
        {
          dashboardId: this.dashboardId,
          typeHint:
            typeof event.data === 'object' && event.data !== null
              ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                String((event.data as any).type)
              : typeof event.data,
        },
        'argus-live:host schema validation failed — message dropped',
      );
      return { kind: 'rejected', reason: 'INVALID_SCHEMA' };
    }

    // 5. Allowlist check on top of the discriminated union. The Zod
    //    schema is already an allowlist, but we restate it explicitly
    //    so any future schema-broadening change cannot silently expand
    //    what this host accepts without an intentional edit here.
    if (!IFRAME_TO_HOST_EVENT_TYPES.includes(parsed.type)) {
      this.logger.warn(
        {
          dashboardId: this.dashboardId,
          type: parsed.type,
        },
        'argus-live:host unknown event type — message dropped',
      );
      return {
        kind: 'rejected',
        reason: 'UNKNOWN_TYPE',
        details: parsed.type,
      };
    }

    // 6. Type-specific dispatch. Each branch is independently testable
    //    and returns its own outcome (so callers can audit which path
    //    accepted a message).
    try {
      switch (parsed.type) {
        case 'READY':
          return this.onReady(parsed);
        case 'FETCH_DATA':
          return await this.onFetchData(parsed);
        case 'EXECUTE_ACTION':
          return await this.onExecuteAction(parsed);
        case 'READ_STATE':
          return await this.onReadState(parsed);
        case 'WRITE_STATE':
          return await this.onWriteState(parsed);
        case 'AUDIT_EVENT':
          return await this.onAuditEvent(parsed);
        case 'RESIZE':
          return this.onResize(parsed);
        case 'ERROR':
          return this.onIframeError(parsed);
        default: {
          // Exhaustiveness check — if a new variant is added to the
          // discriminated union without updating this switch, TypeScript
          // refuses to compile.
          const _exhaustive: never = parsed;
          void _exhaustive;
          return { kind: 'rejected', reason: 'UNKNOWN_TYPE' };
        }
      }
    } catch (err) {
      this.logger.error(
        {
          dashboardId: this.dashboardId,
          err: (err as Error).message,
          type: parsed.type,
        },
        'argus-live:host handler threw — message dropped',
      );
      return {
        kind: 'rejected',
        reason: 'HANDLER_FAILED',
        details: (err as Error).message,
      };
    }
  }

  // ─── Per-type handlers (also exposed for unit testing) ────────────

  /**
   * READY from the iframe. The host *responds* with a Host-direction
   * READY (carrying bindings/classification/theme). AL-B1 does not
   * itself know the bindings — that's stored on the published version
   * row. So we resolve the response asynchronously through the
   * dispatcher. For v1 we route the response by having the React hook
   * provide it via `sendDataResponse` / `sendReady` calls — i.e. the
   * core only logs receipt + signals the hook.
   */
  private onReady(parsed: Extract<IframeToHostEvent, { type: 'READY' }>): HostMessageOutcome {
    this.logger.info(
      {
        dashboardId: this.dashboardId,
        protocolVersion: parsed.protocolVersion,
      },
      'argus-live:host iframe ready',
    );
    return { kind: 'accepted', type: 'READY' };
  }

  private async onFetchData(
    parsed: Extract<IframeToHostEvent, { type: 'FETCH_DATA' }>,
  ): Promise<HostMessageOutcome> {
    // Fetch-rate cap is enforced separately from byte cap. A burst of
    // 31 tiny FETCH_DATAs (each well under 200 KB total) is still a
    // problem — 31 MCP calls in 60s overwhelm AL-B2's proxy budget.
    if (!this.fetchCounter.tryRecord(1)) {
      this.logger.warn(
        {
          dashboardId: this.dashboardId,
          bindingId: parsed.bindingId,
          usage: this.fetchCounter.currentUsage(),
          cap: this.caps.maxFetchesPerWindow,
        },
        'argus-live:host fetch-rate-limit hit — request dropped',
      );
      this.safeSendError(
        'RATE_LIMITED',
        'FETCH_DATA exceeded 30/60s per dashboard',
        parsed.requestId,
        parsed.bindingId,
      );
      this.safeEmitThrottleAudit('fetch');
      return { kind: 'rejected', reason: 'RATE_LIMITED_FETCHES' };
    }

    // Hand off to the AL-B2 dispatcher. On success, send DATA_RESPONSE
    // back to the iframe. On failure, ERROR (caller will surface to the
    // user). Errors here are NOT thrown — they become host errors and
    // the iframe gets a clean structured event.
    try {
      const result = await this.dispatcher.fetchData({
        dashboardId: this.dashboardId,
        bindingId: parsed.bindingId,
        requestId: parsed.requestId,
        params: parsed.params,
      });
      // The dispatcher's classification has the canonical schema (it
      // comes from AL-B2 which uses the platform classification shape);
      // we narrow to the runtime-protocol shape here.
      this.dispatcher.sendToIframe({
        type: 'DATA_RESPONSE',
        requestId: parsed.requestId,
        bindingId: parsed.bindingId,
        data: result.data,
        classification: {
          sensitivity: result.classification.sensitivity as
            | Sensitivity,
          exportControl: result.classification.exportControl as
            | ExportControl,
        },
        dataAsOf: result.dataAsOf,
      });
      return { kind: 'accepted', type: 'FETCH_DATA' };
    } catch (err) {
      this.logger.error(
        {
          dashboardId: this.dashboardId,
          bindingId: parsed.bindingId,
          requestId: parsed.requestId,
          err: (err as Error).message,
        },
        'argus-live:host fetch dispatcher failed',
      );
      this.safeSendError(
        'INTERNAL_ERROR',
        'Data fetch failed',
        parsed.requestId,
        parsed.bindingId,
      );
      return {
        kind: 'rejected',
        reason: 'HANDLER_FAILED',
        details: (err as Error).message,
      };
    }
  }

  /**
   * Cockpit Phase 3 (§2.C) — EXECUTE_ACTION from the iframe. Counts against
   * the SAME fetch-rate budget (an action is at least as costly as a read —
   * it triggers a gated write) so a button-spam loop can't DOS the write
   * path. Hands off to the dispatcher's execute-action call, then relays the
   * outcome back as ACTION_RESULT (never as DATA_RESPONSE). A FORBIDDEN /
   * ERROR outcome is a valid result the iframe renders; a transport throw
   * becomes an ACTION_RESULT with status='error' (so the dashboard's button
   * always settles, never hangs).
   */
  private async onExecuteAction(
    parsed: Extract<IframeToHostEvent, { type: 'EXECUTE_ACTION' }>,
  ): Promise<HostMessageOutcome> {
    if (!this.fetchCounter.tryRecord(1)) {
      this.logger.warn(
        {
          dashboardId: this.dashboardId,
          actionId: parsed.actionId,
          usage: this.fetchCounter.currentUsage(),
          cap: this.caps.maxFetchesPerWindow,
        },
        'argus-live:host action-rate-limit hit — request dropped',
      );
      // Relay a structured ACTION_RESULT (error) so the action settles.
      this.safeSendActionResult({
        requestId: parsed.requestId,
        actionId: parsed.actionId,
        status: 'error',
        code: 'RATE_LIMITED',
        message: 'EXECUTE_ACTION exceeded 30/60s per dashboard',
      });
      this.safeEmitThrottleAudit('fetch');
      return { kind: 'rejected', reason: 'RATE_LIMITED_FETCHES' };
    }

    try {
      const result = await this.dispatcher.executeAction({
        dashboardId: this.dashboardId,
        actionId: parsed.actionId,
        requestId: parsed.requestId,
        inputOverride: parsed.inputOverride,
      });
      this.safeSendActionResult({
        requestId: parsed.requestId,
        actionId: parsed.actionId,
        status: result.status,
        ...(result.approvalId ? { approvalId: result.approvalId } : {}),
        ...(result.result !== undefined ? { result: result.result } : {}),
        ...(result.code ? { code: result.code } : {}),
        ...(result.message ? { message: result.message } : {}),
      });
      return { kind: 'accepted', type: 'EXECUTE_ACTION' };
    } catch (err) {
      this.logger.error(
        {
          dashboardId: this.dashboardId,
          actionId: parsed.actionId,
          requestId: parsed.requestId,
          err: (err as Error).message,
        },
        'argus-live:host execute-action dispatcher failed',
      );
      this.safeSendActionResult({
        requestId: parsed.requestId,
        actionId: parsed.actionId,
        status: 'error',
        code: 'INTERNAL_ERROR',
        message: 'Action execution failed',
      });
      return {
        kind: 'rejected',
        reason: 'HANDLER_FAILED',
        details: (err as Error).message,
      };
    }
  }

  /**
   * Cockpit Phase 4 (§2.A) — READ_STATE from the iframe. Counts against the
   * SAME fetch-rate budget (a state read is at least as costly as a binding
   * read). Maps target → op (rows→read_rows, user_state→read_user_state), hands
   * off to the dispatcher's stateOp, then relays STATE_RESPONSE back. A
   * forbidden/error outcome is a valid result the iframe renders; a transport
   * throw becomes a STATE_RESPONSE with status='error' (so the read always
   * settles, never hangs).
   */
  private async onReadState(
    parsed: Extract<IframeToHostEvent, { type: 'READ_STATE' }>,
  ): Promise<HostMessageOutcome> {
    const op =
      parsed.target === 'rows' ? 'read_rows' : ('read_user_state' as const);
    if (!this.fetchCounter.tryRecord(1)) {
      this.safeSendStateResponse({
        requestId: parsed.requestId,
        op,
        status: 'error',
        code: 'RATE_LIMITED',
        message: 'READ_STATE exceeded 30/60s per dashboard',
      });
      this.safeEmitThrottleAudit('fetch');
      return { kind: 'rejected', reason: 'RATE_LIMITED_FETCHES' };
    }
    try {
      const result = await this.dispatcher.stateOp({
        dashboardId: this.dashboardId,
        requestId: parsed.requestId,
        op,
        tableSlug: parsed.tableSlug,
        ...(parsed.rowKey ? { rowKey: parsed.rowKey } : {}),
        ...(parsed.query ? { query: parsed.query } : {}),
      });
      this.safeSendStateResponse({
        requestId: parsed.requestId,
        op,
        status: result.status,
        ...(result.data !== undefined ? { data: result.data } : {}),
        ...(result.code ? { code: result.code } : {}),
        ...(result.message ? { message: result.message } : {}),
      });
      return { kind: 'accepted', type: 'READ_STATE' };
    } catch (err) {
      this.logger.error(
        {
          dashboardId: this.dashboardId,
          tableSlug: parsed.tableSlug,
          requestId: parsed.requestId,
          err: (err as Error).message,
        },
        'argus-live:host read-state dispatcher failed',
      );
      this.safeSendStateResponse({
        requestId: parsed.requestId,
        op,
        status: 'error',
        code: 'INTERNAL_ERROR',
        message: 'State read failed',
      });
      return {
        kind: 'rejected',
        reason: 'HANDLER_FAILED',
        details: (err as Error).message,
      };
    }
  }

  /**
   * Cockpit Phase 4 (§2.A) — WRITE_STATE from the iframe. Same fetch-rate
   * budget. Maps target → op (row→write_row, user_state→write_user_state). The
   * server sets organization_id (the iframe NEVER writes the DB) and
   * classification-gates the write.
   */
  private async onWriteState(
    parsed: Extract<IframeToHostEvent, { type: 'WRITE_STATE' }>,
  ): Promise<HostMessageOutcome> {
    const op =
      parsed.target === 'row' ? 'write_row' : ('write_user_state' as const);
    if (!this.fetchCounter.tryRecord(1)) {
      this.safeSendStateResponse({
        requestId: parsed.requestId,
        op,
        status: 'error',
        code: 'RATE_LIMITED',
        message: 'WRITE_STATE exceeded 30/60s per dashboard',
      });
      this.safeEmitThrottleAudit('fetch');
      return { kind: 'rejected', reason: 'RATE_LIMITED_FETCHES' };
    }
    try {
      const result = await this.dispatcher.stateOp({
        dashboardId: this.dashboardId,
        requestId: parsed.requestId,
        op,
        tableSlug: parsed.tableSlug,
        ...(parsed.rowKey ? { rowKey: parsed.rowKey } : {}),
        data: parsed.data,
        ...(parsed.classification
          ? { classification: parsed.classification }
          : {}),
      });
      this.safeSendStateResponse({
        requestId: parsed.requestId,
        op,
        status: result.status,
        ...(result.data !== undefined ? { data: result.data } : {}),
        ...(result.code ? { code: result.code } : {}),
        ...(result.message ? { message: result.message } : {}),
      });
      return { kind: 'accepted', type: 'WRITE_STATE' };
    } catch (err) {
      this.logger.error(
        {
          dashboardId: this.dashboardId,
          tableSlug: parsed.tableSlug,
          requestId: parsed.requestId,
          err: (err as Error).message,
        },
        'argus-live:host write-state dispatcher failed',
      );
      this.safeSendStateResponse({
        requestId: parsed.requestId,
        op,
        status: 'error',
        code: 'INTERNAL_ERROR',
        message: 'State write failed',
      });
      return {
        kind: 'rejected',
        reason: 'HANDLER_FAILED',
        details: (err as Error).message,
      };
    }
  }

  private async onAuditEvent(
    parsed: Extract<IframeToHostEvent, { type: 'AUDIT_EVENT' }>,
  ): Promise<HostMessageOutcome> {
    // Redundant allowlist on eventType — the Zod schema already pins
    // this to three values, but we restate it (see §6 above for the
    // same rationale on the outer type allowlist).
    if (!PostMessageHost.AUDIT_EVENT_TYPES.has(parsed.eventType)) {
      this.logger.warn(
        {
          dashboardId: this.dashboardId,
          eventType: parsed.eventType,
        },
        'argus-live:host disallowed audit event type — dropped',
      );
      return {
        kind: 'rejected',
        reason: 'UNKNOWN_TYPE',
        details: parsed.eventType,
      };
    }
    try {
      await this.dispatcher.emitAudit({
        dashboardId: this.dashboardId,
        eventType: parsed.eventType,
        metadata: parsed.metadata,
      });
      return { kind: 'accepted', type: 'AUDIT_EVENT' };
    } catch (err) {
      this.logger.error(
        {
          dashboardId: this.dashboardId,
          eventType: parsed.eventType,
          err: (err as Error).message,
        },
        'argus-live:host audit dispatcher failed',
      );
      return {
        kind: 'rejected',
        reason: 'HANDLER_FAILED',
        details: (err as Error).message,
      };
    }
  }

  private onResize(
    parsed: Extract<IframeToHostEvent, { type: 'RESIZE' }>,
  ): HostMessageOutcome {
    // The schema already enforces 0 ≤ heightPx ≤ 20 000; we still
    // clamp here so a future loosening of the schema cannot let the
    // iframe push the host viewport. The hook reads `lastResize` to
    // apply the height; if the hook is not mounted yet we just drop
    // (idempotent — the iframe will RESIZE again on its next paint).
    const clamped = Math.min(
      Math.max(parsed.heightPx, 0),
      this.caps.maxIframeHeightPx,
    );
    this.logger.info(
      {
        dashboardId: this.dashboardId,
        heightPx: clamped,
      },
      'argus-live:host iframe resize',
    );
    // Notify subscribers via the resize callback if installed.
    this.lastResizePx = clamped;
    if (this.onResizeCallback) this.onResizeCallback(clamped);
    return { kind: 'accepted', type: 'RESIZE' };
  }

  private onIframeError(
    parsed: Extract<IframeToHostEvent, { type: 'ERROR' }>,
  ): HostMessageOutcome {
    this.logger.error(
      {
        dashboardId: this.dashboardId,
        message: parsed.message,
        stack: parsed.stack,
      },
      'argus-live:host iframe reported an error',
    );
    // Emit a `dashboard.runtime.crashed` audit event so the activity log
    // captures the failure (best-effort; ignore dispatch failures).
    void this.dispatcher
      .emitAudit({
        dashboardId: this.dashboardId,
        eventType: 'dashboard.runtime.crashed',
        metadata: { message: parsed.message },
      })
      .catch((err: unknown) => {
        this.logger.error(
          {
            dashboardId: this.dashboardId,
            err: (err as Error).message,
          },
          'argus-live:host audit emit failed for iframe error',
        );
      });
    return { kind: 'accepted', type: 'ERROR' };
  }

  // ─── Helpers used by the hook layer ───────────────────────────────

  /** The last resize height the host has seen, used by the hook to
   *  drive the iframe outer-container height. */
  lastResizePx: number | null = null;

  /** Optional callback hook into resize events — the React hook
   *  registers this to drive its `lastResize` state. */
  onResizeCallback: ((heightPx: number) => void) | null = null;

  /** Diagnostic snapshot — exposed for the hook's UI badges. */
  snapshot() {
    return {
      dashboardId: this.dashboardId,
      bound: this.expectedSource !== null,
      byteUsage: this.byteCounter.currentUsage(),
      fetchUsage: this.fetchCounter.currentUsage(),
      lastResizePx: this.lastResizePx,
    };
  }

  // ─── Internal: best-effort error sends ────────────────────────────

  /**
   * Send a Host→Iframe ERROR. Wrapped so a throwing dispatcher
   * (synchronous send) cannot blow up the message handler.
   */
  private safeSendError(
    code:
      | 'CLASSIFICATION_DENIED'
      | 'PERMISSION_DENIED'
      | 'RATE_LIMITED'
      | 'TOOL_UNAVAILABLE'
      | 'INVALID_REQUEST'
      | 'INTERNAL_ERROR',
    message: string,
    requestId?: string,
    bindingId?: string,
  ): void {
    try {
      this.dispatcher.sendToIframe({
        type: 'ERROR',
        code,
        message,
        ...(requestId ? { requestId } : {}),
        ...(bindingId ? { bindingId } : {}),
      });
    } catch (err) {
      this.logger.error(
        {
          dashboardId: this.dashboardId,
          err: (err as Error).message,
        },
        'argus-live:host failed to send Host→Iframe ERROR',
      );
    }
  }

  /**
   * Cockpit Phase 3 (§2.C) — send a Host→Iframe ACTION_RESULT. Wrapped so a
   * throwing synchronous send cannot blow up the message handler.
   */
  private safeSendActionResult(input: {
    requestId: string;
    actionId: string;
    status: 'executed' | 'pending_approval' | 'forbidden' | 'error';
    approvalId?: string;
    result?: unknown;
    code?: string;
    message?: string;
  }): void {
    try {
      this.dispatcher.sendToIframe({
        type: 'ACTION_RESULT',
        requestId: input.requestId,
        actionId: input.actionId,
        status: input.status,
        ...(input.approvalId ? { approvalId: input.approvalId } : {}),
        ...(input.result !== undefined ? { result: input.result } : {}),
        ...(input.code ? { code: input.code } : {}),
        ...(input.message ? { message: input.message } : {}),
      });
    } catch (err) {
      this.logger.error(
        { dashboardId: this.dashboardId, err: (err as Error).message },
        'argus-live:host failed to send Host→Iframe ACTION_RESULT',
      );
    }
  }

  /**
   * Cockpit Phase 4 (§2.A) — send a Host→Iframe STATE_RESPONSE. Wrapped so a
   * throwing synchronous send cannot blow up the message handler.
   */
  private safeSendStateResponse(input: {
    requestId: string;
    op: 'read_rows' | 'write_row' | 'read_user_state' | 'write_user_state';
    status: 'ok' | 'forbidden' | 'error';
    data?: unknown;
    code?: string;
    message?: string;
  }): void {
    try {
      this.dispatcher.sendToIframe({
        type: 'STATE_RESPONSE',
        requestId: input.requestId,
        op: input.op,
        status: input.status,
        ...(input.data !== undefined ? { data: input.data } : {}),
        ...(input.code ? { code: input.code } : {}),
        ...(input.message ? { message: input.message } : {}),
      });
    } catch (err) {
      this.logger.error(
        { dashboardId: this.dashboardId, err: (err as Error).message },
        'argus-live:host failed to send Host→Iframe STATE_RESPONSE',
      );
    }
  }

  /**
   * Emit `dashboard.runtime.throttled` to the activity log. Best-effort
   * — if AL-B2 is down we don't want the rate-limit check itself to
   * throw.
   */
  private safeEmitThrottleAudit(kind: 'byte' | 'fetch'): void {
    this.dispatcher
      .emitAudit({
        dashboardId: this.dashboardId,
        eventType: 'dashboard.runtime.throttled',
        metadata: { kind, ts: this.now() },
      })
      .catch((err: unknown) => {
        this.logger.error(
          {
            dashboardId: this.dashboardId,
            err: (err as Error).message,
          },
          'argus-live:host throttle audit emit failed',
        );
      });
  }
}

// ─── UTF-8 byte length helper ───────────────────────────────────────────

/**
 * Returns the UTF-8 byte length of a string. Uses TextEncoder when
 * available (every browser since 2017 + Node 12+); falls back to a
 * Blob round-trip for paranoia. The Blob path is used in some
 * jsdom-less vitest environments where TextEncoder may not be
 * polyfilled.
 */
function encoderByteLength(s: string): number {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(s).length;
  }
  // Fallback: count code points (worst case 4 bytes/char).
  // Slight over-estimate is acceptable — the cap is a safety bound,
  // not an accounting one.
  let bytes = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) bytes += 1;
    else if (c < 0x800) bytes += 2;
    else if (c >= 0xd800 && c <= 0xdbff) {
      bytes += 4;
      i++; // skip surrogate
    } else bytes += 3;
  }
  return bytes;
}

// Export for unit tests; not part of the public surface but the
// `__test__` suffix on the alias signals "internal".
export const __testables = { encoderByteLength };
